import sum from 'lodash/sum';
import type { BufferingMode } from '../../enums';
import type { DestroyOptions, LuminaryObject } from '../../types';
import calculateReadingDurations from '../../libs/calculateReadingDurations';
import { assertHasOwnProperties, assertObject } from '../../validators';
import { Config } from '../../video-data/config';
import { BaseAsset } from '../asset/BaseAsset';
import { ConcurrentTimeline } from '../../libs/ConcurrentTimeline';
import { Subslide } from './Subslide';

export class SubslideManager extends BaseAsset implements LuminaryObject {
    override _classType = 'SubslideManager';

    _timeline = new ConcurrentTimeline({
        name: 'subscenes_timeline',
        duration: Config.DEFAULT_SCENE_DURATION,
    });

    /**
     * Flag which indicates loading of properties is in progress
     * We need it to prevent `_checkAndFixProperties` call during `load` process in order to prevent recursive call of`_updateSubslideDurationBasedOnTextLength`
     */
    #isVideoDataLoading = false;
    /**
     * Duration which we set on slide level.
     * We need it in order not to apply intermediate duration in `_checkAndFixProperties` during updating slide.duration
     */
    _durationToApply: number = null;

    /**
     * Called while seeking, forwarding a request to buffer any data (if need be) down the hierarchy.
     */
    async _propagateBuffering(bufferingMode: BufferingMode) {
        const activeSubscenes = this.slide.getActiveSubscenesAt(this.slide.currentTime);
        await Promise.all(
            activeSubscenes.map(async (subscene) => {
                return subscene._propagateBuffering(bufferingMode);
            }),
        );
    }

    override get canPlay() {
        return this._childAssets.every((asset) => asset.canPlay);
    }

    /**
     * This shouldn't be needed, but adding as the interface requires it. Ideally the
     * SubslideManager isn't a "luminary object", but that requires a larger refactor.
     */
    _emitAssetsCanPlay() {
        console.warn(
            'SubslideManager should not be forwarding canPlay events, the subslide' +
                ' should be sending these events to the slide. Forwarding for now anyway.',
        );
        if (this.slide) {
            this.slide.emit('assetsCanPlay');
        }
    }

    /**
     * Returns true if all assets (deeply) within this object have finished loading, or have
     * failed to load. If any assets are still loading or retrying a load this will be false.
     */
    get areAssetsDoneTryingToLoad(): boolean {
        return this.subscenes.every((subscene) => subscene.areAssetsDoneTryingToLoad);
    }

    /**
     * Returns true if all assets (deeply) within this object have successfully loaded.
     */
    override get isLoaded(): boolean {
        return this._childAssets.every((asset) => asset.isLoaded);
    }

    override get shouldLoad() {
        return true;
    }

    get duration(): number {
        return this.slide.duration;
    }

    /**
     * @deprecated use {@link subscenes} instead
     * */
    get subslides(): Subslide[] {
        return this.subscenes;
    }

    get subscenes(): Subslide[] {
        return this._childAssets as Subslide[];
    }

    /**
     * Create and add a new subslide. By default, it will add the subslide at the end.
     * @param duplicateContent - if content should be copied from the first subslide
     * @param index - if provided, the index where to insert the subslide
     * @param id - if provided, the id of the newly created subslide
     * @returns the newly created Subslide
     */
    addSubslide(duplicateContent = true, index?: number, id?: string, grow = false) {
        const duration = this.duration;
        if (duration === 0) {
            throw Error('Invalid slide duration');
        }
        if (typeof index === 'undefined') {
            index = this.subslides.length;
        }
        const sourceSubslide = this.subslides[0];

        const subslide = new Subslide(this, {
            id,
            updateParentOnRedraw: true,
            fetchResources: this.fetchResources,
            autoDraw: this.autoDraw,
        });

        if (this.slide) {
            // Add the new sublide group's timeline to the slide's timeline without changing the slide's duration
            this._timeline.add(subslide._timeline, index, grow);
        }

        // copy base properties
        if (duplicateContent && sourceSubslide) {
            this.#copySubslideContentFromSourceToTarget(sourceSubslide, subslide);
        }

        // addChild triggers a redraw so we have to do all of the wiring beforehand which is why this is run last.
        this._addAssetAt(subslide, index);
        return subslide;
    }

    /**
     * duplicates our scene's base subslide configuration onto the given subslide
     */
    #copySubslideContentFromSourceToTarget(sourceSubslide: Subslide, targetSubSlide: Subslide) {
        assertObject(sourceSubslide);
        assertObject(targetSubSlide);

        // transfer over all child content
        const childrenConfig = sourceSubslide.saveChildren();
        targetSubSlide.load(childrenConfig);

        targetSubSlide.generateNewAssetIds();

        // update all text content to be blank (clear text)
        // we do this because when duplicating a subslide we don't want to copy over text
        targetSubSlide.getAssetsByType(Config.TEXT_ASSET_TYPE).forEach((asset) => {
            asset.text = '';
        });
    }

    removeSubslide(index: number) {
        if (!this.subslides.hasOwnProperty(index)) {
            return;
        }
        this._timeline.remove(this.subslides[index]._timeline);
        this.subslides[index].destroy(true);
    }

    generateNewAssetIds() {
        this.subscenes.forEach((subscene) => {
            subscene.generateNewAssetIds();
        });
    }

    override clear() {
        const nsubslides = this.subslides.length;
        for (let i = 0; i < nsubslides; i++) {
            this.removeSubslide(0);
        }
        this._durationToApply = null;
        this.#isVideoDataLoading = false;
    }

    override _checkAndFixProperties(): void {
        super._checkAndFixProperties();

        if (this.#isVideoDataLoading) {
            return;
        }

        if (!this.#hasAssetWithTextEditingEnabled) {
            const duration = this._durationToApply ?? this.duration;
            this._durationToApply = null;
            // in the _build method, we don't want to mess with the autoDraw setting
            // so we set the `batch` parameter to false.
            this._updateSubslideDurationBasedOnTextLength(duration, false);
        }

        this.#removeAnimationEndAnchorsFromLastSubslide();
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this.subslides.forEach((subslide) => subslide.destroy(options));
    }

    override _build() {}

    get #hasAssetWithTextEditingEnabled() {
        return this.slide
            .getAssetsByType(Config.TEXT_ASSET_TYPE)
            .some((textAsset) => textAsset.hasTextEditingEnabled);
    }

    _updateSubslideDurationBasedOnTextLength(duration: number, batch = true) {
        const initAutoDraw = this.autoDraw;
        if (batch) {
            this.autoDraw = false;
        }

        const textLengths = this.#getTextLengthArrayFromTextAssetChildren();
        const durations = calculateReadingDurations(textLengths, duration);
        durations.forEach((duration, index) => {
            this.subslides[index].duration = duration;
        });

        if (batch) {
            this.autoDraw = initAutoDraw;
        }
    }

    #getTextLengthArrayFromTextAssetChildren() {
        return this.subslides.map((subslide) => {
            return sum(
                subslide
                    .getAssetsByType(Config.TEXT_ASSET_TYPE)
                    .map((textAsset) => textAsset.text.length),
            );
        });
    }

    #removeAnimationEndAnchorsFromLastSubslide() {
        const _applyAnchorsToRemove = (isLastSubslide: boolean, asset: BaseAsset) => {
            if (
                isLastSubslide &&
                asset.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING
            ) {
                asset._animationAnchorsToRemove = Config.END_ANIMATION_CURVE_ANCHOR;
            } else {
                asset._animationAnchorsToRemove = Config.NONE_ANIMATION_CURVE_ANCHOR;
            }
        };

        this.subslides.forEach((subslide, index) => {
            const isLastSubslide = index === this.subslides.length - 1;
            _applyAnchorsToRemove(isLastSubslide, subslide);
            subslide.getAssets().forEach((child: BaseAsset) => {
                _applyAnchorsToRemove(isLastSubslide, child);
            });
        });
    }

    override load(data: { subslides?: Record<string, unknown>[] } = {}) {
        assertObject(data);
        assertHasOwnProperties(data, ['subslides']);

        if (this.subslides.length !== 0) {
            throw Error('Unable to load data, subslide length is not zero.');
        }

        const initAutoDraw = this.autoDraw;
        this.autoDraw = false;

        this.#isVideoDataLoading = true;

        data.subslides.forEach((subslideProperties, index) => {
            const subslide = this.addSubslide(
                false,
                index,
                subslideProperties.id as string | undefined,
                true,
            );
            subslide.load(subslideProperties);
        });

        this.#isVideoDataLoading = false;

        this.autoDraw = initAutoDraw;
        return this;
    }

    override save(allProperties = true, clone = false) {
        const data: { subslides: Record<string, unknown>[] } = { subslides: [] };
        this.subslides.forEach((subslide) =>
            data.subslides.push(subslide.save(allProperties, clone)),
        );
        return data;
    }
}
